import { FC, ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import styles from './donut.module.sass';
import { int } from "@/lib/format";

export interface DonutSegment {
  /** Category label (shown in legend) */
  label: string;
  /** Raw value — share is computed internally */
  value: number;
  /** Optional explicit color; otherwise auto from --gt-c1..c8 */
  color?: string;
  /** Optional secondary value displayed in the legend (e.g. monetary amount) */
  hint?: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  /** Outer diameter in px (default 160) */
  size?: number;
  /** Stroke width (default size × 0.16) */
  stroke?: number;
  /** Center label content (defaults to "{count} categories") */
  center?: ReactNode;
  /** Hide legend (default false) */
  hideLegend?: boolean;
  /** Show legend on the right (default) or below */
  legendPlacement?: 'right' | 'bottom';
  className?: string;
}

const PALETTE = [
  'var(--gt-c1)', 'var(--gt-c2)', 'var(--gt-c3)', 'var(--gt-c4)',
  'var(--gt-c5)', 'var(--gt-c6)', 'var(--gt-c7)', 'var(--gt-c8)',
];

/**
 * Donut — Glasstape DS premium categorical-share chart.
 *
 * Spec source: /Glasstape/storybook/stories-dataviz.jsx :: STORIES.donut
 *
 * Audit checklist:
 *   ✓ Only --gt-* tokens (palette + text + border)
 *   ✓ Tabular-nums on every numeric (.legendValue / .legendPct / .centerValue)
 *   ✓ Stroke-linecap round, segments separated by 2° gap
 *   ✓ Hover brightens segment (--gt-dur-sm, --gt-ease)
 *   ✓ Center mono value scales with size (size × 0.24)
 *   ✓ No inline styles other than dynamic stroke offsets
 *   ✓ Light/dark via tokens
 *   ✓ ARIA: role="img" + aria-label summarizing categories
 */
export const Donut: FC<DonutProps> = ({
  segments,
  size = 160,
  stroke,
  center,
  hideLegend = false,
  legendPlacement = 'right',
  className,
}) => {
  const strokeW = stroke ?? Math.max(8, Math.round(size * 0.16));
  const r = (size - strokeW) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const GAP_DEG = 2;
  const gapLen = (GAP_DEG / 360) * circumference;

  const items = useMemo(() => {
    const total = segments.reduce((acc, s) => acc + Math.max(0, s.value), 0) || 1;
    let offset = 0;
    return segments
      .filter((s) => s.value > 0)
      .map((s, i) => {
        const share = s.value / total;
        const segLen = Math.max(0, share * circumference - gapLen);
        const item = {
          ...s,
          share,
          color: s.color || PALETTE[i % PALETTE.length],
          dashArray: `${segLen} ${circumference - segLen}`,
          dashOffset: -offset,
        };
        offset += share * circumference;
        return item;
      });
  }, [segments, circumference, gapLen]);

  const total = segments.reduce((acc, s) => acc + Math.max(0, s.value), 0);
  const centerValueSize = Math.round(size * 0.24);

  const ariaLabel = `Donut chart: ${items.map((it) => `${it.label} ${Math.round(it.share * 100)}%`).join(', ')}`;

  return (
    <div
      className={classNames('gt-scope', styles.wrap, className)}
      style={legendPlacement === 'bottom' ? { flexDirection: 'column', alignItems: 'flex-start' } : undefined}
    >
      <div className={styles.figure} style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={styles.svg}
          role="img"
          aria-label={ariaLabel}
        >
          {/* Track */}
          <circle cx={c} cy={c} r={r} strokeWidth={strokeW} className={styles.track} />

          {/* Rotated -90deg so segments start at 12 o'clock */}
          <g transform={`rotate(-90 ${c} ${c})`}>
            {items.map((it) => (
              <circle
                key={it.label}
                cx={c}
                cy={c}
                r={r}
                stroke={it.color}
                strokeWidth={strokeW}
                strokeDasharray={it.dashArray}
                strokeDashoffset={it.dashOffset}
                className={styles.seg}
              />
            ))}
          </g>
        </svg>

        <div className={styles.center}>
          {center ?? (
            <>
              <span className={styles.centerValue} style={{ fontSize: centerValueSize }}>
                {items.length}
              </span>
              <span className={styles.centerLabel}>
                {items.length === 1 ? 'category' : 'categories'}
              </span>
            </>
          )}
        </div>
      </div>

      {!hideLegend && (
        <ul className={styles.legend}>
          {items.map((it) => (
            <li key={it.label} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: it.color }} />
              <span className={styles.legendLabel} title={it.label}>{it.label}</span>
              <span className={styles.legendValue}>{it.hint ?? formatRaw(it.value)}</span>
              <span className={styles.legendPct}>{(it.share * 100).toFixed(1)}%</span>
            </li>
          ))}
          {total === 0 && (
            <li className={styles.legendItem}>
              <span className={styles.legendLabel} style={{ gridColumn: '1 / -1', color: 'var(--gt-text-muted)' }}>
                No segments.
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

function formatRaw(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return int(v);
}

Donut.displayName = 'Donut';
export default Donut;
